import { useCallback, useEffect, useRef, useState } from 'react';

// Generate subtle tones using Web Audio API — no external audio files needed
const createAudioContext = () => {
  try {
    return new (window.AudioContext || window.webkitAudioContext)();
  } catch {
    return null;
  }
};

/**
 * Play a short tone using Web Audio API
 * @param {AudioContext} ctx
 * @param {object} options - frequency, duration, type, volume
 */
const playTone = (ctx, { frequency = 440, duration = 0.15, type = 'sine', volume = 0.15, delay = 0 } = {}) => {
  if (!ctx) return;
  try {
    const oscillator = ctx?.createOscillator();
    const gainNode = ctx?.createGain();
    oscillator?.connect(gainNode);
    gainNode?.connect(ctx?.destination);
    oscillator.type = type;
    oscillator?.frequency?.setValueAtTime(frequency, ctx?.currentTime + delay);
    gainNode?.gain?.setValueAtTime(0, ctx?.currentTime + delay);
    gainNode?.gain?.linearRampToValueAtTime(volume, ctx?.currentTime + delay + 0.01);
    gainNode?.gain?.exponentialRampToValueAtTime(0.001, ctx?.currentTime + delay + duration);
    oscillator?.start(ctx?.currentTime + delay);
    oscillator?.stop(ctx?.currentTime + delay + duration + 0.05);
  } catch {
    // Silently fail if audio context is suspended or unavailable
  }
};

/**
 * Sound presets for different event types
 */
const SOUNDS = {
  message: (ctx) => {
    // Two-note gentle chime: C5 → E5
    playTone(ctx, { frequency: 523.25, duration: 0.18, volume: 0.12, delay: 0 });
    playTone(ctx, { frequency: 659.25, duration: 0.22, volume: 0.10, delay: 0.12 });
  },
  match: (ctx) => {
    // Three-note ascending chime: G4 → B4 → D5
    playTone(ctx, { frequency: 392, duration: 0.15, volume: 0.12, delay: 0 });
    playTone(ctx, { frequency: 493.88, duration: 0.15, volume: 0.11, delay: 0.1 });
    playTone(ctx, { frequency: 587.33, duration: 0.25, volume: 0.10, delay: 0.2 });
  },
  notification: (ctx) => {
    // Single soft ping: A4
    playTone(ctx, { frequency: 440, duration: 0.2, volume: 0.10, delay: 0 });
  }
};

/**
 * useSoundNotifications
 * Returns { isMuted, toggleMute, playSound }
 * Mute state is persisted in localStorage under 'soundNotificationsMuted'
 */
const useSoundNotifications = () => {
  const [isMuted, setIsMuted] = useState(() => {
    try {
      return localStorage.getItem('soundNotificationsMuted') === 'true';
    } catch {
      return false;
    }
  });

  const audioCtxRef = useRef(null);

  // Lazily create AudioContext on first interaction
  const getAudioContext = useCallback(() => {
    if (!audioCtxRef?.current) {
      audioCtxRef.current = createAudioContext();
    }
    // Resume if suspended (browser autoplay policy)
    if (audioCtxRef?.current?.state === 'suspended') {
      audioCtxRef?.current?.resume()?.catch(() => {});
    }
    return audioCtxRef?.current;
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const next = !prev;
      try {
        localStorage.setItem('soundNotificationsMuted', String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  /**
   * playSound(type: 'message' | 'match' | 'notification')
   */
  const playSound = useCallback((type = 'notification') => {
    if (isMuted) return;
    const ctx = getAudioContext();
    const soundFn = SOUNDS?.[type] || SOUNDS?.notification;
    soundFn(ctx);
  }, [isMuted, getAudioContext]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioCtxRef?.current?.close()?.catch(() => {});
    };
  }, []);

  return { isMuted, toggleMute, playSound };
};

export default useSoundNotifications;
